import React, { useCallback } from "react";
import { createPortal } from "react-dom";
import {
  Button,
  Checkbox,
  DatePicker,
  Iconography,
  IconographySizes,
} from "@hid-galaxy-ui/galaxy-react";
import {
  Autocomplete,
  TextField as MuiTextField,
  Popper,
  Checkbox as MuiCheckbox,
  Chip,
} from "@mui/material";
import "../../../../../create-new-partner/partner-detail-form/PartnerDetailForm.scss";
import "./IntegrationFilterModal.scss";
import {
  EMPTY_INTEGRATION_FILTER,
  EMPTY_STAGE_FILTER,
  getStageFilter,
  IntegrationFilterState,
  IntegrationStageDateRange,
  IntegrationStageFilterFields,
  isValidationStage,
  pruneEmptyStageFilters,
} from "./integrationFilterTypes";
import { createEmptyStageFilter } from "../integrationStageFilterUtils";
import FilterChipDeleteIcon from "../../FilterChipDeleteIcon";
import { useIntegrationFilterOptions } from "./useIntegrationFilterOptions";

const FILTER_DRAWER_Z_INDEX = 1300;

const parseDate = (value: string | null | undefined): Date | null =>
  value ? new Date(value) : null;

const FilterAutocompletePopper = (
  props: React.ComponentProps<typeof Popper>
) => (
  <Popper
    {...props}
    style={{ ...props.style, zIndex: FILTER_DRAWER_Z_INDEX }}
    placement="bottom-start"
  />
);

const FilterFieldDivider: React.FC = () => (
  <hr className="integration-filter-field-divider" aria-hidden="true" />
);

const SearchableMultiSelectField: React.FC<{
  id: string;
  label: string;
  placeholder: string;
  options: string[];
  selectedValues: string[];
  onChange: (values: string[]) => void;
}> = ({ id, label, placeholder, options, selectedValues, onChange }) => (
  <div className="label-field-container partner-searchable-multiselect partner-searchable-multiselect--tags-below integration-filter-multiselect">
    <label className="field-label" htmlFor={id}>
      {label}
    </label>
    <Autocomplete
      id={id}
      multiple
      disableCloseOnSelect
      options={options}
      value={selectedValues}
      onChange={(_, value) => onChange(value)}
      PopperComponent={FilterAutocompletePopper}
      popupIcon={
        <Iconography icon="angleDown" size={IconographySizes.Small} />
      }
      renderOption={(props, option, { selected }) => (
        <li {...props} key={option}>
          <MuiCheckbox size="small" checked={selected} />
          {option}
        </li>
      )}
      renderTags={() => null}
      renderInput={(params) => (
        <MuiTextField {...params} placeholder={placeholder} />
      )}
    />
    {selectedValues.length > 0 && (
      <div className="partner-filter-selected-tags">
        {selectedValues.map((option) => (
          <Chip
            key={option}
            label={option}
            size="small"
            deleteIcon={<FilterChipDeleteIcon />}
            onDelete={() =>
              onChange(selectedValues.filter((value) => value !== option))
            }
          />
        ))}
      </div>
    )}
  </div>
);

type FilterSelectOption = {
  label: string;
  value: string;
};

const SearchableOptionMultiSelectField: React.FC<{
  id: string;
  label: string;
  placeholder: string;
  options: FilterSelectOption[];
  selectedValues: string[];
  onChange: (values: string[]) => void;
}> = ({ id, label, placeholder, options, selectedValues, onChange }) => {
  const optionByValue = React.useMemo(
    () => new Map(options.map((option) => [option.value, option.label])),
    [options]
  );

  return (
    <div className="label-field-container partner-searchable-multiselect partner-searchable-multiselect--tags-below integration-filter-multiselect">
      <label className="field-label" htmlFor={id}>
        {label}
      </label>
      <Autocomplete
        id={id}
        multiple
        disableCloseOnSelect
        options={options}
        value={options.filter((option) => selectedValues.includes(option.value))}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        getOptionLabel={(option) => option.label}
        onChange={(_, value) => onChange(value.map((option) => option.value))}
        PopperComponent={FilterAutocompletePopper}
        popupIcon={
          <Iconography icon="angleDown" size={IconographySizes.Small} />
        }
        renderOption={(props, option, { selected }) => (
          <li {...props} key={option.value}>
            <MuiCheckbox size="small" checked={selected} />
            {option.label}
          </li>
        )}
        renderTags={() => null}
        renderInput={(params) => (
          <MuiTextField {...params} placeholder={placeholder} />
        )}
      />
      {selectedValues.length > 0 && (
        <div className="partner-filter-selected-tags">
          {selectedValues.map((value) => (
            <Chip
              key={value}
              label={optionByValue.get(value) ?? value}
              size="small"
              deleteIcon={<FilterChipDeleteIcon />}
              onDelete={() =>
                onChange(selectedValues.filter((item) => item !== value))
              }
            />
          ))}
        </div>
      )}
    </div>
  );
};

const IntegrationStageDateFields: React.FC<{
  stage: string;
  range: IntegrationStageDateRange;
  onStageDateChange: (
    stage: string,
    field: StageDateKey,
    date: Date | [Date | null, Date | null] | null
  ) => void;
}> = ({ stage, range, onStageDateChange }) => (
  <div className="partner-form-grid">
    <div className="partner-registration-date-field">
      <label className="field-label" htmlFor={`${stage}-startDateFrom`}>
        Start Date From
      </label>
      <DatePicker
        label=""
        onChange={(date) => onStageDateChange(stage, "startDateFrom", date)}
        format="dd/MM/yyyy"
        placeholder="dd/mm/yyyy"
        id={`${stage}-startDateFrom`}
        name={`${stage}-startDateFrom`}
        value={parseDate(range.startDateFrom)}
        isClearable
      />
    </div>
    <div className="partner-registration-date-field">
      <label className="field-label" htmlFor={`${stage}-startDateTo`}>
        Start Date To
      </label>
      <DatePicker
        label=""
        onChange={(date) => onStageDateChange(stage, "startDateTo", date)}
        format="dd/MM/yyyy"
        placeholder="dd/mm/yyyy"
        id={`${stage}-startDateTo`}
        name={`${stage}-startDateTo`}
        value={parseDate(range.startDateTo)}
        isClearable
      />
    </div>
    <div className="partner-registration-date-field">
      <label className="field-label" htmlFor={`${stage}-endDateFrom`}>
        End Date From
      </label>
      <DatePicker
        label=""
        onChange={(date) => onStageDateChange(stage, "endDateFrom", date)}
        format="dd/MM/yyyy"
        placeholder="dd/mm/yyyy"
        id={`${stage}-endDateFrom`}
        name={`${stage}-endDateFrom`}
        value={parseDate(range.endDateFrom)}
        isClearable
      />
    </div>
    <div className="partner-registration-date-field">
      <label className="field-label" htmlFor={`${stage}-endDateTo`}>
        End Date To
      </label>
      <DatePicker
        label=""
        onChange={(date) => onStageDateChange(stage, "endDateTo", date)}
        format="dd/MM/yyyy"
        placeholder="dd/mm/yyyy"
        id={`${stage}-endDateTo`}
        name={`${stage}-endDateTo`}
        value={parseDate(range.endDateTo)}
        isClearable
      />
    </div>
  </div>
);

const IntegrationStageFilterPanel: React.FC<{
  stage: string;
  fields: IntegrationStageFilterFields;
  validationStatusOptions: string[];
  integrationStatusOptions: string[];
  validationScoreOperators: string[];
  endorsementLevelOptions: string[];
  onRemove: () => void;
  onFieldsChange: (fields: IntegrationStageFilterFields) => void;
  onStageDateChange: (
    stage: string,
    field: StageDateKey,
    date: Date | [Date | null, Date | null] | null
  ) => void;
}> = ({
  stage,
  fields,
  validationStatusOptions,
  integrationStatusOptions,
  validationScoreOperators,
  endorsementLevelOptions,
  onRemove,
  onFieldsChange,
  onStageDateChange,
}) => {
  const isValidation = isValidationStage(stage);

  return (
    <div className="integration-filter-stage-block">
      <Chip
        className="integration-filter-stage-block__chip"
        label={stage}
        size="small"
        deleteIcon={<FilterChipDeleteIcon />}
        onDelete={onRemove}
      />

      {isValidation ? (
        <>
          <div className="label-field-container">
            <label className="field-label" htmlFor={`${stage}-validationScoreValue`}>
              Validation Score
            </label>
            <div className="integration-filter-score-row">
              <div className="label-field-container partner-searchable-multiselect integration-filter-score-operator">
                <Autocomplete
                  id={`${stage}-validationScoreOperator`}
                  className="integration-filter-score-operator__select"
                  options={validationScoreOperators}
                  value={fields.validationScoreOperator}
                  onChange={(_, value) =>
                    onFieldsChange({
                      ...fields,
                      validationScoreOperator: value ?? ">",
                    })
                  }
                  disableClearable
                  openOnFocus
                  PopperComponent={FilterAutocompletePopper}
                  popupIcon={
                    <Iconography icon="angleDown" size={IconographySizes.Small} />
                  }
                  renderInput={(params) => (
                    <MuiTextField
                      {...params}
                      inputProps={{
                        ...params.inputProps,
                        readOnly: true,
                      }}
                    />
                  )}
                />
              </div>
              <div className="label-field-container integration-filter-score-value">
                <MuiTextField
                  id={`${stage}-validationScoreValue`}
                  placeholder="Enter score"
                  value={fields.validationScoreValue}
                  onChange={(event) =>
                    onFieldsChange({
                      ...fields,
                      validationScoreValue: event.target.value,
                    })
                  }
                  fullWidth
                />
              </div>
            </div>
          </div>

          <FilterFieldDivider />

          <SearchableMultiSelectField
            id={`${stage}-endorsementLevel`}
            label="Endorsement level"
            placeholder="Select"
            selectedValues={fields.endorsementLevel}
            options={endorsementLevelOptions}
            onChange={(values) =>
              onFieldsChange({ ...fields, endorsementLevel: values })
            }
          />

          <FilterFieldDivider />

          <SearchableMultiSelectField
            id={`${stage}-validationStageStatus`}
            label={`${stage} Stage Status`}
            placeholder="Select"
            selectedValues={fields.validationStatuses}
            options={validationStatusOptions}
            onChange={(values) =>
              onFieldsChange({ ...fields, validationStatuses: values })
            }
          />
        </>
      ) : (
        <SearchableMultiSelectField
          id={`${stage}-integrationStageStatus`}
          label={`${stage} Stage Status`}
          placeholder="Select"
          selectedValues={fields.integrationStatuses}
          options={integrationStatusOptions}
          onChange={(values) =>
            onFieldsChange({ ...fields, integrationStatuses: values })
          }
        />
      )}

      <FilterFieldDivider />

      <IntegrationStageDateFields
        stage={stage}
        range={fields.dateRange}
        onStageDateChange={onStageDateChange}
      />
    </div>
  );
};

const IntegrationStageFiltersBlock: React.FC<{
  integrationFilter: IntegrationFilterState;
  integrationStageOptions: string[];
  validationStatusOptions: string[];
  integrationStatusOptions: string[];
  validationScoreOperators: string[];
  endorsementLevelOptions: string[];
  onStagesChange: (stages: string[]) => void;
  onStageFieldsChange: (
    stage: string,
    fields: IntegrationStageFilterFields
  ) => void;
  onStageDateChange: (
    stage: string,
    field: StageDateKey,
    date: Date | [Date | null, Date | null] | null
  ) => void;
}> = ({
  integrationFilter,
  integrationStageOptions,
  validationStatusOptions,
  integrationStatusOptions,
  validationScoreOperators,
  endorsementLevelOptions,
  onStagesChange,
  onStageFieldsChange,
  onStageDateChange,
}) => (
  <div className="integration-filter-stages-field">
    <div className="label-field-container partner-searchable-multiselect">
      <label className="field-label" htmlFor="integrationStages">
        Integration Stages
      </label>
      <Autocomplete
        id="integrationStages"
        multiple
        disableCloseOnSelect
        options={integrationStageOptions}
        value={integrationFilter.integrationStages}
        onChange={(_, value) => onStagesChange(value)}
        PopperComponent={FilterAutocompletePopper}
        popupIcon={
          <Iconography icon="angleDown" size={IconographySizes.Small} />
        }
        renderOption={(props, option, { selected }) => (
          <li {...props} key={option}>
            <MuiCheckbox size="small" checked={selected} />
            {option}
          </li>
        )}
        renderTags={() => null}
        renderInput={(params) => (
          <MuiTextField {...params} placeholder="Select" />
        )}
      />
    </div>

    {integrationFilter.integrationStages.length > 0 && (
      <div className="integration-filter-stages-field__dates">
        {integrationFilter.integrationStages.map((stage, index) => (
          <React.Fragment key={stage}>
            {index > 0 && <FilterFieldDivider />}
            <IntegrationStageFilterPanel
              stage={stage}
              fields={getStageFilter(integrationFilter, stage)}
              validationStatusOptions={validationStatusOptions}
              integrationStatusOptions={integrationStatusOptions}
              validationScoreOperators={validationScoreOperators}
              endorsementLevelOptions={endorsementLevelOptions}
              onRemove={() =>
                onStagesChange(
                  integrationFilter.integrationStages.filter(
                    (value) => value !== stage
                  )
                )
              }
              onFieldsChange={(fields) => onStageFieldsChange(stage, fields)}
              onStageDateChange={onStageDateChange}
            />
          </React.Fragment>
        ))}
      </div>
    )}
  </div>
);

const FilterCheckbox: React.FC<{
  label: string;
  checked: boolean;
  onToggle: () => void;
}> = ({ label, checked, onToggle }) => (
  <div
    className="partner-filter-drawer__checkbox-item"
    role="button"
    tabIndex={0}
    onClick={onToggle}
    onKeyDown={(e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onToggle();
      }
    }}
  >
    <Checkbox
      name={label}
      value={label}
      checked={checked}
      id={`integration-filter-checkbox-${label}`}
      onChange={() => {}}
    />
  </div>
);

const FilterSection: React.FC<{
  title: string;
  children: React.ReactNode;
}> = ({ title, children }) => (
  <section className="partner-form-section">
    <h3 className="partner-form-section__title">{title}</h3>
    <div className="partner-form-section__card">{children}</div>
  </section>
);

type StageDateKey = keyof IntegrationStageDateRange;

interface IntegrationFilterModalProps {
  integrationFilterProp: IntegrationFilterState;
  setIntegrationFilterProp: (filter: IntegrationFilterState) => void;
  setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const formatDateForStorage = (date: Date | null): string | null =>
  date ? date.toLocaleDateString("en-CA") : null;

const IntegrationFilterModal: React.FC<IntegrationFilterModalProps> = ({
  integrationFilterProp,
  setIntegrationFilterProp,
  setModalOpen,
}) => {
  const [integrationFilter, setIntegrationFilter] =
    React.useState(integrationFilterProp);

  React.useEffect(() => {
    setIntegrationFilter(integrationFilterProp);
  }, [integrationFilterProp]);
  const {
    regionOptions,
    partnerTypeOptions,
    consultantOptions,
    technologyOptions,
    validationStatusOptions,
    validationScoreOperators,
    endorsementLevelOptions,
    integrationTypeOptions,
    integrationStatusOptions,
    integrationStageOptions,
  } = useIntegrationFilterOptions();

  React.useEffect(() => {
    document.body.classList.add("partner-filter-drawer-open");
    return () => {
      document.body.classList.remove("partner-filter-drawer-open");
    };
  }, []);

  const toggleArrayValue = useCallback(
    (field: keyof IntegrationFilterState, value: string) => {
      setIntegrationFilter((prev) => {
        const current = prev[field];
        if (!Array.isArray(current)) return prev;

        const nextValues = current.includes(value)
          ? current.filter((item) => item !== value)
          : [...current, value];

        return { ...prev, [field]: nextValues };
      });
    },
    []
  );

  const handleStagesChange = useCallback((stages: string[]) => {
    setIntegrationFilter((prev) => {
      const nextStageFilters = { ...prev.stageFilters };

      stages.forEach((stage) => {
        if (!nextStageFilters[stage]) {
          nextStageFilters[stage] = createEmptyStageFilter();
        }
      });

      Object.keys(nextStageFilters).forEach((stage) => {
        if (!stages.includes(stage)) {
          delete nextStageFilters[stage];
        }
      });

      return {
        ...prev,
        integrationStages: stages,
        stageFilters: nextStageFilters,
      };
    });
  }, []);

  const handleStageFieldsChange = useCallback(
    (stage: string, fields: IntegrationStageFilterFields) => {
      setIntegrationFilter((prev) => ({
        ...prev,
        stageFilters: {
          ...prev.stageFilters,
          [stage]: fields,
        },
      }));
    },
    []
  );

  const handleStageDateChange = useCallback(
    (
      stage: string,
      field: StageDateKey,
      date: Date | [Date | null, Date | null] | null
    ) => {
      const actualDate = Array.isArray(date) ? date[0] : date;
      const formatted = formatDateForStorage(actualDate);

      setIntegrationFilter((prev) => {
        const current = prev.stageFilters[stage] ?? {
          ...EMPTY_STAGE_FILTER,
        };

        return {
          ...prev,
          stageFilters: {
            ...prev.stageFilters,
            [stage]: {
              ...current,
              dateRange: {
                ...current.dateRange,
                [field]: formatted,
              },
            },
          },
        };
      });
    },
    []
  );

  const onClearAllClick = useCallback(() => {
    setIntegrationFilter({ ...EMPTY_INTEGRATION_FILTER });
  }, []);

  const onApplyClick = useCallback(() => {
    setIntegrationFilterProp(pruneEmptyStageFilters(integrationFilter));
    setModalOpen(false);
  }, [integrationFilter, setIntegrationFilterProp, setModalOpen]);

  return createPortal(
    <>
      <div className="partner-filter-drawer__backdrop" aria-hidden="true" />
      <div
        className="partner-filter-drawer partner-filter-drawer__panel"
        role="dialog"
        aria-modal="true"
        aria-label="Filter Integration"
      >
        <div className="partner-filter-drawer__header">
          <div className="partner-filter-drawer__header-title">
            <span
              className="partner-filter-drawer__filter-icon"
              aria-hidden="true"
            >
              <Iconography icon="sliders" size={IconographySizes.Medium} />
            </span>
            <span>Filter Integration</span>
          </div>
          <button
            type="button"
            className="partner-filter-drawer__close-btn"
            onClick={() => setModalOpen(false)}
            aria-label="Close filter panel"
          >
            <Iconography icon="xmark" size={IconographySizes.Medium} />
          </button>
        </div>

        <div className="partner-filter-drawer__body partner-detail-form-container partner-detail-form-container--modal">
          <FilterSection title="Partner Details">
            <SearchableMultiSelectField
              id="integrationPartnerType"
              label="Partner Type"
              placeholder="Select"
              selectedValues={integrationFilter.partnerType}
              options={partnerTypeOptions}
              onChange={(values) =>
                setIntegrationFilter((prev) => ({
                  ...prev,
                  partnerType: values,
                }))
              }
            />

            <FilterFieldDivider />

            <div className="label-field-container">
              <span className="field-label">Region</span>
              <div className="partner-filter-drawer__checkbox-grid partner-filter-drawer__checkbox-grid--region">
                {regionOptions.map((item) => (
                  <FilterCheckbox
                    key={item}
                    label={item}
                    checked={integrationFilter.region.includes(item)}
                    onToggle={() => toggleArrayValue("region", item)}
                  />
                ))}
              </div>
            </div>
          </FilterSection>

          <FilterSection title="Technical Consultant">
            <SearchableOptionMultiSelectField
              id="integrationConsultantId"
              label="Consultant Name"
              placeholder="Select"
              selectedValues={integrationFilter.consultantId}
              options={consultantOptions}
              onChange={(values) =>
                setIntegrationFilter((prev) => ({
                  ...prev,
                  consultantId: values,
                }))
              }
            />
          </FilterSection>

          <FilterSection title="Technology">
            <div className="label-field-container">
              <span className="field-label">Technology</span>
              <div className="partner-filter-drawer__checkbox-grid">
                {technologyOptions.map((item) => (
                  <FilterCheckbox
                    key={item}
                    label={item}
                    checked={integrationFilter.technology.includes(item)}
                    onToggle={() => toggleArrayValue("technology", item)}
                  />
                ))}
              </div>
            </div>

            <FilterFieldDivider />

            <SearchableMultiSelectField
              id="integrationType"
              label="Integration Type"
              placeholder="Select"
              selectedValues={integrationFilter.integrationType}
              options={integrationTypeOptions}
              onChange={(values) =>
                setIntegrationFilter((prev) => ({
                  ...prev,
                  integrationType: values,
                }))
              }
            />
          </FilterSection>

          <FilterSection title="Integration">
            <IntegrationStageFiltersBlock
              integrationFilter={integrationFilter}
              integrationStageOptions={integrationStageOptions}
              validationStatusOptions={validationStatusOptions}
              integrationStatusOptions={integrationStatusOptions}
              validationScoreOperators={validationScoreOperators}
              endorsementLevelOptions={endorsementLevelOptions}
              onStagesChange={handleStagesChange}
              onStageFieldsChange={handleStageFieldsChange}
              onStageDateChange={handleStageDateChange}
            />
          </FilterSection>
        </div>

        <div className="partner-filter-drawer__footer">
          <Button
            variant="secondary"
            label="CANCEL"
            onClick={() => setModalOpen(false)}
          />
          <div className="partner-filter-drawer__footer-right">
            <Button
              variant="secondary"
              label="RESET"
              onClick={onClearAllClick}
            />
            <Button variant="primary" label="APPLY" onClick={onApplyClick} />
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

export default IntegrationFilterModal;
